<template>
  <div ref="root" class="wc" :class="`wc--${type}`" @mouseleave="hover = null">
    <!-- Barras horizontales: HTML, para que las etiquetas largas se corten bien. -->
    <div v-if="type === 'bar'" class="wc-hbars">
      <div
        v-for="(p, idx) in points"
        :key="p.label"
        class="wc-hbar"
        @mouseenter="hover = idx"
      >
        <span class="wc-hbar__label" :title="p.label">{{ p.label }}</span>
        <span class="wc-hbar__track">
          <span class="wc-hbar__fill" :class="{ 'is-other': p.other }" :style="{ width: `${pct(p.value)}%` }"></span>
        </span>
        <span class="wc-hbar__value">{{ format(p.value) }}</span>
      </div>
    </div>

    <!-- Torta / dona: escalones del primario y leyenda con valores (no solo color). -->
    <div v-else-if="type === 'pie' || type === 'donut'" class="wc-pie">
      <svg :width="pieSize" :height="pieSize" :viewBox="`0 0 ${pieSize} ${pieSize}`" class="wc-pie__svg" role="img">
        <path
          v-for="(s, idx) in slices"
          :key="s.label"
          :d="s.path"
          class="wc-slice"
          :style="{ fill: sliceColor(idx, s) }"
          :class="{ 'is-dim': hover !== null && hover !== idx }"
          @mouseenter="hover = idx"
        >
          <title>{{ s.label }}: {{ format(s.value) }} ({{ s.share }}%)</title>
        </path>
        <text v-if="type === 'donut'" :x="pieSize / 2" :y="pieSize / 2" class="wc-pie__total" text-anchor="middle" dominant-baseline="central">
          {{ format(total) }}
        </text>
      </svg>
      <ul class="wc-legend">
        <li
          v-for="(s, idx) in slices"
          :key="s.label"
          class="wc-legend__item"
          :class="{ 'is-active': hover === idx }"
          @mouseenter="hover = idx"
        >
          <span class="wc-legend__swatch" :style="{ background: sliceColor(idx, s) }"></span>
          <span class="wc-legend__label" :title="s.label">{{ s.label }}</span>
          <span class="wc-legend__value">{{ format(s.value) }} · {{ s.share }}%</span>
        </li>
      </ul>
    </div>

    <!-- Columnas / línea / área: SVG medido al tamaño real del widget. -->
    <template v-else>
      <svg v-if="width > 0 && height > 0" :width="width" :height="height" class="wc-svg" role="img">
        <g class="wc-grid">
          <g v-for="t in yTicks" :key="t.value">
            <line :x1="plot.left" :x2="plot.right" :y1="t.y" :y2="t.y" />
            <text :x="plot.left - 6" :y="t.y" text-anchor="end" dominant-baseline="central">{{ formatShort(t.value) }}</text>
          </g>
        </g>
        <g v-if="type === 'column'">
          <rect
            v-for="(p, idx) in points"
            :key="p.label"
            :x="xBand(idx) + bandPad"
            :y="yPos(p.value)"
            :width="Math.max(1, band - bandPad * 2)"
            :height="Math.max(0, plot.bottom - yPos(p.value))"
            :rx="Math.min(4, (band - bandPad * 2) / 2)"
            class="wc-col"
            :class="{ 'is-other': p.other, 'is-dim': hover !== null && hover !== idx }"
          />
        </g>
        <g v-else>
          <path v-if="type === 'area'" :d="areaPath" class="wc-area" />
          <path :d="linePath" class="wc-line" />
          <circle
            v-for="(p, idx) in points"
            :key="p.label"
            :cx="xMid(idx)"
            :cy="yPos(p.value)"
            :r="hover === idx ? 5 : points.length > 24 ? 0 : 3"
            class="wc-dot"
          />
          <line v-if="hover !== null" :x1="xMid(hover)" :x2="xMid(hover)" :y1="plot.top" :y2="plot.bottom" class="wc-crosshair" />
        </g>
        <g class="wc-xlabels">
          <text
            v-for="(p, idx) in points"
            v-show="showXLabel(idx)"
            :key="p.label"
            :x="xMid(idx)"
            :y="plot.bottom + 14"
            text-anchor="middle"
          >{{ shortLabel(p.label) }}</text>
        </g>
        <!-- Zonas de hover más grandes que la marca. -->
        <rect
          v-for="(p, idx) in points"
          :key="`hit-${p.label}`"
          :x="xBand(idx)"
          :y="plot.top"
          :width="band"
          :height="plot.bottom - plot.top"
          class="wc-hit"
          @mouseenter="hover = idx"
        />
      </svg>
      <div
        v-if="hover !== null && points[hover]"
        class="wc-tip"
        :style="tipStyle"
      >
        <span class="wc-tip__label">{{ points[hover].label }}</span>
        <span class="wc-tip__value">{{ format(points[hover].value) }}</span>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * Gráfico liviano para los widgets (sin librería): barras horizontales, columnas,
 * línea, área, torta y dona. Una sola serie: `points = [{ label, value, other? }]`.
 * Colores: el primario del tenant (--bs-primary); la torta usa escalones de ese
 * mismo color y siempre lleva leyenda con valores.
 */
import { formatNumberSync } from "@/utils/numberFormatter";
import { formatCompactNumber } from "../widgetData";

// Escalones del primario: el último sigue visible sobre el fondo oscuro.
const PIE_STEPS = [1, 0.82, 0.67, 0.54, 0.43, 0.34, 0.27, 0.21];

export default {
  name: "WidgetChart",
  props: {
    type: { type: String, default: "column" },
    points: { type: Array, default: () => [] },
  },
  data() {
    return { width: 0, height: 0, hover: null };
  },
  computed: {
    max() {
      return Math.max(0, ...this.points.map((p) => p.value));
    },
    min() {
      return Math.min(0, ...this.points.map((p) => p.value));
    },
    total() {
      return this.points.reduce((s, p) => s + Math.max(0, p.value), 0);
    },
    plot() {
      return { left: 44, right: this.width - 8, top: 10, bottom: this.height - 22 };
    },
    band() {
      return this.points.length ? (this.plot.right - this.plot.left) / this.points.length : 0;
    },
    bandPad() {
      return Math.min(8, this.band * 0.18);
    },
    niceMax() {
      return niceCeil(this.max);
    },
    yTicks() {
      const top = this.niceMax || 1;
      return [0, 0.5, 1].map((f) => ({ value: top * f, y: this.yPos(top * f) }));
    },
    linePath() {
      return this.points.map((p, i) => `${i ? "L" : "M"}${this.xMid(i)},${this.yPos(p.value)}`).join(" ");
    },
    areaPath() {
      if (!this.points.length) return "";
      const first = this.xMid(0);
      const last = this.xMid(this.points.length - 1);
      return `${this.linePath} L${last},${this.plot.bottom} L${first},${this.plot.bottom} Z`;
    },
    pieSize() {
      return 132;
    },
    slices() {
      const size = this.pieSize;
      const r = size / 2 - 2;
      const inner = this.type === "donut" ? r * 0.58 : 0;
      const c = size / 2;
      const total = this.total || 1;
      let angle = -Math.PI / 2;
      return this.points
        .filter((p) => p.value > 0)
        .map((p) => {
          const sweep = (p.value / total) * Math.PI * 2;
          const path = arcPath(c, c, r, inner, angle, angle + sweep);
          angle += sweep;
          return { ...p, path, share: Math.round((p.value / total) * 1000) / 10 };
        });
    },
    tipStyle() {
      if (this.hover === null) return {};
      const x = this.xMid(this.hover);
      const left = Math.min(Math.max(x, 60), this.width - 60);
      return { left: `${left}px`, top: "4px" };
    },
  },
  mounted() {
    if (typeof ResizeObserver !== "undefined") {
      this.ro = new ResizeObserver(() => this.measure());
      this.ro.observe(this.$refs.root);
    }
    this.measure();
  },
  beforeDestroy() {
    if (this.ro) this.ro.disconnect();
  },
  methods: {
    measure() {
      const el = this.$refs.root;
      if (!el) return;
      this.width = el.clientWidth;
      this.height = Math.max(120, el.clientHeight);
    },
    pct(v) {
      return this.max > 0 ? Math.max(2, Math.round((Math.max(0, v) / this.max) * 100)) : 0;
    },
    xBand(i) {
      return this.plot.left + i * this.band;
    },
    xMid(i) {
      return this.xBand(i) + this.band / 2;
    },
    yPos(v) {
      const top = this.niceMax || 1;
      const h = this.plot.bottom - this.plot.top;
      return this.plot.bottom - (Math.max(0, v) / top) * h;
    },
    showXLabel(i) {
      // Cuántas etiquetas entran sin pisarse (~56 px cada una).
      const fit = Math.max(1, Math.floor((this.plot.right - this.plot.left) / 56));
      const step = Math.ceil(this.points.length / fit);
      return i % step === 0;
    },
    shortLabel(label) {
      const s = String(label);
      return s.length > 10 ? `${s.slice(0, 9)}…` : s;
    },
    sliceColor(idx, s) {
      if (s.other) return "var(--dw-line-strong)";
      return `rgba(var(--bs-primary-rgb), ${PIE_STEPS[Math.min(idx, PIE_STEPS.length - 1)]})`;
    },
    format(v) {
      return formatNumberSync(v) || String(v);
    },
    formatShort(v) {
      return formatCompactNumber(v);
    },
  },
};

function niceCeil(v) {
  if (v <= 0) return 0;
  const exp = Math.pow(10, Math.floor(Math.log10(v)));
  const f = v / exp;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return nice * exp;
}

function arcPath(cx, cy, r, inner, a0, a1) {
  // Un círculo entero no se puede dibujar con un solo arco.
  if (a1 - a0 >= Math.PI * 2 - 1e-6) a1 = a0 + Math.PI * 2 - 1e-4;
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const p = (rad, a) => [cx + rad * Math.cos(a), cy + rad * Math.sin(a)];
  const [x0, y0] = p(r, a0);
  const [x1, y1] = p(r, a1);
  if (!inner) return `M${cx},${cy} L${x0},${y0} A${r},${r} 0 ${large} 1 ${x1},${y1} Z`;
  const [ix1, iy1] = p(inner, a1);
  const [ix0, iy0] = p(inner, a0);
  return `M${x0},${y0} A${r},${r} 0 ${large} 1 ${x1},${y1} L${ix1},${iy1} A${inner},${inner} 0 ${large} 0 ${ix0},${iy0} Z`;
}
</script>

<style scoped>
.wc { position: relative; width: 100%; height: 100%; min-height: 120px; }
.wc--bar, .wc--pie, .wc--donut { height: auto; min-height: 0; }

.wc-hbars { display: flex; flex-direction: column; gap: 6px; }
.wc-hbar { display: grid; grid-template-columns: minmax(70px, 38%) 1fr auto; align-items: center; gap: 8px; font-size: 0.8rem; }
.wc-hbar__label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wc-hbar__track { height: 10px; border-radius: 5px; background: var(--dw-surface-2); overflow: hidden; }
.wc-hbar__fill { display: block; height: 100%; border-radius: 0 4px 4px 0; background: var(--bs-primary); }
.wc-hbar__fill.is-other { background: var(--dw-line-strong); }
.wc-hbar__value { font-weight: 600; text-align: right; }

.wc-svg { display: block; overflow: visible; }
.wc-grid line { stroke: var(--dw-line); stroke-width: 1; }
.wc-grid text, .wc-xlabels text { fill: var(--dw-muted); font-size: 10px; }
.wc-col { fill: var(--bs-primary); transition: opacity 0.12s ease; }
.wc-col.is-other { fill: var(--dw-line-strong); }
.wc-col.is-dim { opacity: 0.55; }
.wc-line { fill: none; stroke: var(--bs-primary); stroke-width: 2; stroke-linejoin: round; stroke-linecap: round; }
.wc-area { fill: rgba(var(--bs-primary-rgb), 0.16); }
.wc-dot { fill: var(--bs-primary); stroke: var(--dw-surface); stroke-width: 2; }
.wc-crosshair { stroke: var(--dw-line-strong); stroke-width: 1; stroke-dasharray: 3 3; }
.wc-hit { fill: transparent; }
.wc-tip {
  position: absolute;
  transform: translateX(-50%);
  pointer-events: none;
  background: var(--dw-surface);
  border: 1px solid var(--dw-border);
  border-radius: 6px;
  box-shadow: var(--dw-shadow);
  padding: 4px 8px;
  font-size: 0.75rem;
  display: flex;
  gap: 8px;
  white-space: nowrap;
  z-index: 2;
}
.wc-tip__label { color: var(--dw-muted); }
.wc-tip__value { font-weight: 600; color: var(--dw-text); }

.wc-pie { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.wc-pie__svg { flex-shrink: 0; }
.wc-slice { stroke: var(--dw-surface); stroke-width: 2; transition: opacity 0.12s ease; }
.wc-slice.is-dim { opacity: 0.45; }
.wc-pie__total { fill: var(--dw-text); font-weight: 700; font-size: 15px; }
.wc-legend { list-style: none; margin: 0; padding: 0; flex: 1; min-width: 140px; display: flex; flex-direction: column; gap: 4px; }
.wc-legend__item { display: grid; grid-template-columns: 10px 1fr auto; align-items: center; gap: 8px; font-size: 0.78rem; padding: 1px 4px; border-radius: 4px; }
.wc-legend__item.is-active { background: var(--dw-surface-2); }
.wc-legend__swatch { width: 10px; height: 10px; border-radius: 3px; }
.wc-legend__label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wc-legend__value { color: var(--dw-muted); white-space: nowrap; }
</style>
